
import { defineComponent, PropType, ref, computed, watch } from 'vue'
import { formatDateWithFormat } from "@/core/helpers/DatetimeConverter";
type GameOption = {
  id: number|string
  leagueId: number|string
  leagueName: string
  homeTeamName: string
  awayTeamName: string,
  gameDate: string
}

export default defineComponent({
  name: 'GamePicker',
  props: {
    open: { type: Boolean, default: false },
    modelValue: { type: [Array, Number, String, null] as PropType<any>, default: null },
    mode: { type: String as PropType<'single'|'multiple'>, default: 'multiple' },
    options: { type: Array as PropType<GameOption[]>, default: () => [] }
  },
  emits: ['update:open', 'update:modelValue'],
  setup (props, { emit }) {
    const q = ref('')
    const activeLeagueId = ref<number|string|null>(null)
    const selected = ref<Set<number|string>>(new Set())
    const cardsEl = ref<HTMLDivElement|null>(null)

    watch(() => props.open, (o) => {
      if (!o) return
      selected.value = new Set(
        Array.isArray(props.modelValue) ? props.modelValue
          : (props.modelValue != null ? [props.modelValue] : [])
      )
      if (props.options.length && activeLeagueId.value == null) {
        activeLeagueId.value = props.options[0].leagueId
      }
      // reset scroll στην αρχή
      requestAnimationFrame(() => { if (cardsEl.value) cardsEl.value.scrollLeft = 0 })
    }, { immediate: true })

    const leagues = computed(() => {
      const map = new Map<string|number, string>()
      for (const g of props.options) map.set(g.leagueId, g.leagueName)
      return Array.from(map, ([id, name]) => ({ id, name }))
    })

    
    const filteredLeagues = computed(() => {
      const t = q.value.trim().toLowerCase()
      if (!t) return leagues.value
      return leagues.value.filter(l =>
        l.name.toLowerCase().includes(t) ||
        props.options.some(o =>
          o.leagueId === l.id &&
          (`${o.homeTeamName} ${o.awayTeamName}`).toLowerCase().includes(t)
        )
      )
    })

    const activeLeague = computed(() =>
      leagues.value.find(l => l.id === activeLeagueId.value) || null
    )

    const leagueMatches = computed(() =>
      props.options.filter(o => o.leagueId === activeLeagueId.value)
    )

    const selectedList = computed(() => {
      const ids = selected.value
      return props.options.filter(o => ids.has(o.id))
    })

    const setLeague = (id: number|string) => { activeLeagueId.value = id }
    const isSelected = (id: number|string) => selected.value.has(id)

    const toggle = (id: number|string) => {
      if (props.mode === 'single') {
        selected.value = new Set([id])
        return
      }
      const s = new Set(selected.value)
      s.has(id) ? s.delete(id) : s.add(id)
      selected.value = s
    }

    const clearSel = () => { selected.value = new Set() }
    const close = () => emit('update:open', false)
    const formatDateCustom = ((date) => {
        return formatDateWithFormat(date,"D MMM H:mm");
    });
    const confirm = () => {
      const arr = Array.from(selected.value)
      emit('update:modelValue', props.mode === 'single' ? (arr[0] ?? null) : arr)
      emit('update:open', false)
    }

    const scroll = (dir: 1 | -1) => {
      const el = cardsEl.value
      if (!el) return
      const step = Math.max(260, el.clientWidth * 0.8) // πόσο να “γλιστράει”
      el.scrollBy({ left: dir * step, behavior: 'smooth' })
    }

    return {
      q, activeLeagueId, leagues, filteredLeagues, activeLeague,
      leagueMatches, selected, selectedList, cardsEl,
      setLeague, isSelected, toggle, clearSel, close, confirm, scroll,formatDateCustom
    }
  }
})
